// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useFormatCoin } from '@mysten/core';
import { useCurrentAccount } from '@mysten/dapp-kit';
import { Heading, Text } from '@mysten/ui';
import { Link } from 'react-router-dom';

import { StakeModal } from '~/components/StakeModal';
import { SelectValidatorModal } from '~/components/SelectValidator';
import { useCpcStakeFlow } from '~/hooks/useCpcStakeFlow';
import { useCpcStakePositions } from '~/hooks/useCpcStakePositions';
import { CPC_SYMBOL, DGC_TYPE_ARG } from '~/utils/cpcCoin';

export function TotalStaked({ address }: { address: string }) {
	const account = useCurrentAccount();
	const isOwnAddress =
		account?.address?.toLowerCase() === address.toLowerCase();
	const flow = useCpcStakeFlow();
	const { data: positions } = useCpcStakePositions(address);

	const totalActivePendingStake =
		positions?.reduce((acc, pos) => {
			try {
				return acc + BigInt(pos.principalMist || '0');
			} catch {
				return acc;
			}
		}, 0n) ?? 0n;
	const fssCount = positions?.filter((p) => p.kind === 'fungible_staked_sui').length ?? 0;

	const [formatted] = useFormatCoin(totalActivePendingStake, DGC_TYPE_ARG);

	return (
		<div className="flex flex-wrap items-center gap-3">
			<div className="flex min-w-44 items-center gap-3 rounded-xl bg-white/10 border border-white/20 px-4 py-3 backdrop-blur-sm">
				<div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#38BDF8]/20 text-[#38BDF8] text-xs font-bold">
					{CPC_SYMBOL.slice(0, 1)}
				</div>
				<div className="flex flex-col">
					<Text variant="pBody/semibold" color="dark" uppercase>
						已质押
					</Text>
					<Heading variant="heading6/semibold" color="blue" as="div">
						{formatted} {CPC_SYMBOL}
					</Heading>
					<span className="text-[10px] text-[#94a3b8]">本金，提取收益不变</span>
					{fssCount > 0 ? (
						<span className="text-[10px] text-[#94a3b8]">含 {fssCount} 笔收益凭证</span>
					) : null}
				</div>
				{isOwnAddress ? (
					<button
						type="button"
						onClick={flow.openStakeModal}
						className="ml-auto bg-[#38BDF8] text-white rounded-lg px-2.5 py-1.5 text-xs font-semibold hover:bg-[#38BDF8]/90"
					>
						质押
					</button>
				) : null}
			</div>
			<Link to="/stake" className="text-xs text-[#38BDF8] hover:underline">
				质押中心
			</Link>

			{isOwnAddress ? (
				<>
					<StakeModal
						isOpen={flow.isStakeOpen}
						onClose={() => flow.setIsStakeOpen(false)}
						balance={flow.availableCpc}
						stakeableBalance={flow.stakeableCpc}
						symbol={CPC_SYMBOL}
						onConfirm={(amount, addrs) =>
							flow.handleStakeModalConfirm(
								amount,
								addrs.map((a) => ({ address: a })),
							)
						}
					/>
					<SelectValidatorModal
						isOpen={flow.isValidatorOpen}
						validators={flow.validatorOptions}
						onClose={() => flow.setIsValidatorOpen(false)}
						onSelect={flow.executeStake}
					/>
				</>
			) : null}
		</div>
	);
}
